import React, { useState } from 'react';
import { Table, Button, Space, Modal, Form, Input, Select, message, Popconfirm, Tag, Alert, Card, Spin, Checkbox, Divider, Typography, Empty, Tabs } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, UserAddOutlined, StopOutlined, CheckCircleOutlined, TeamOutlined, BankOutlined, KeyOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import { GET_ALL_USERS, GET_ALL_ORGANIZATIONS, GET_ORG_MEMBERSHIPS, SEARCH_USER_BY_PHONE_OR_EMAIL, GET_ALL_LOCATIONS, GET_USER_MEMBERSHIPS } from '../graphql/queries';
import { CREATE_USER, UPDATE_USER, DELETE_USER, CREATE_MEMBERSHIP, UPDATE_MEMBERSHIP, REVOKE_MEMBERSHIP } from '../graphql/mutations';
import { useAuth } from '../hooks/useAuth';

const { Option } = Select;
const { Text } = Typography;

// ==================== OWNER STAFF VIEW ====================
const OwnerStaffView: React.FC<{ organizationId: string; organizationName: string }> = ({ organizationId, organizationName }) => {
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingMembership, setEditingMembership] = useState<any>(null);
  const [searchType, setSearchType] = useState<'phone' | 'email'>('email');
  const [searchValue, setSearchValue] = useState('');
  const [foundUser, setFoundUser] = useState<any>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<'STAFF' | 'OWNER'>('STAFF');
  const [selectedParkingIds, setSelectedParkingIds] = useState<string[]>([]);

  // Query for organization memberships
  const { loading, data, refetch } = useQuery(GET_ORG_MEMBERSHIPS, {
    variables: { organizationId },
    fetchPolicy: 'cache-and-network',
  });

  // Query for parkings in this organization
  const { data: parkingsData } = useQuery(GET_ALL_LOCATIONS, {
    variables: { type: 'PARKING', organizationId },
  });

  // Lazy query for searching users
  const [searchUser, { loading: searching }] = useLazyQuery(SEARCH_USER_BY_PHONE_OR_EMAIL, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data?.searchUserByPhoneOrEmail) {
        setFoundUser(data.searchUserByPhoneOrEmail);
        setSearchError(null);
      } else {
        setFoundUser(null);
        setSearchError('User not found. They must register first before being added as staff.');
      }
    },
    onError: (error) => {
      setFoundUser(null);
      setSearchError(error.message);
    },
  });

  // Mutations
  const [createMembership, { loading: creating }] = useMutation(CREATE_MEMBERSHIP);
  const [updateMembership, { loading: updating }] = useMutation(UPDATE_MEMBERSHIP);
  const [revokeMembership] = useMutation(REVOKE_MEMBERSHIP);

  const parkings = parkingsData?.getAllLocations || [];
  const memberships = data?.getOrgMemberships || [];

  const handleSearch = () => {
    if (!searchValue.trim()) {
      message.warning('Please enter a search value');
      return;
    }

    setFoundUser(null);
    setSearchError(null);

    searchUser({
      variables: {
        organizationId,
        ...(searchType === 'phone' ? { phone: searchValue.trim() } : { email: searchValue.trim() }),
      },
    });
  };

  const handleAddStaff = async () => {
    if (!foundUser) return;

    try {
      await createMembership({
        variables: {
          input: {
            userId: foundUser.id,
            organizationId,
            role: selectedRole,
            parkingIds: selectedParkingIds.length > 0 ? selectedParkingIds : null,
          },
        },
      });

      message.success(`${foundUser.email} has been added as ${selectedRole}`);
      setIsAddModalVisible(false);
      resetAddForm();
      refetch();
    } catch (error: any) {
      message.error(error.message);
    }
  };

  const handleEditMembership = async () => {
    if (!editingMembership) return;

    try {
      await updateMembership({
        variables: {
          id: editingMembership.id,
          input: {
            role: selectedRole,
            parkingIds: selectedParkingIds.length > 0 ? selectedParkingIds : null,
          },
        },
      });

      message.success('Staff updated successfully');
      setIsEditModalVisible(false);
      refetch();
    } catch (error: any) {
      message.error(error.message);
    }
  };

  const handleToggleActive = async (membership: any) => {
    try {
      await updateMembership({
        variables: {
          id: membership.id,
          input: {
            isActive: !membership.isActive,
          },
        },
      });

      message.success(membership.isActive ? 'Staff member disabled' : 'Staff member enabled');
      refetch();
    } catch (error: any) {
      message.error(error.message);
    }
  };

  const handleRemoveMembership = async (membershipId: string) => {
    try {
      await revokeMembership({
        variables: { id: membershipId },
      });

      message.success('Staff member removed');
      refetch();
    } catch (error: any) {
      message.error(error.message);
    }
  };

  const resetAddForm = () => {
    setSearchType('email');
    setSearchValue('');
    setFoundUser(null);
    setSearchError(null);
    setSelectedRole('STAFF');
    setSelectedParkingIds([]);
  };

  const openEditModal = (membership: any) => {
    setEditingMembership(membership);
    setSelectedRole(membership.role);
    setSelectedParkingIds(membership.parkingIds || []);
    setIsEditModalVisible(true);
  };

  const columns = [
    {
      title: 'Name',
      key: 'name',
      render: (_: any, record: any) => {
        const name = `${record.user?.firstName || ''} ${record.user?.lastName || ''}`.trim();
        return name || '-';
      },
    },
    {
      title: 'Email',
      key: 'email',
      render: (_: any, record: any) => record.user?.email || '-',
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => {
        const colorMap: Record<string, string> = {
          OWNER: 'blue',
          STAFF: 'green',
          ADMIN: 'red',
        };
        return <Tag color={colorMap[role]}>{role}</Tag>;
      },
    },
    {
      title: 'Parkings',
      key: 'parkings',
      render: (_: any, record: any) => {
        if (!record.parkingIds || record.parkingIds.length === 0) {
          return <Tag color="purple">All Parkings</Tag>;
        }
        return (
          <Space wrap>
            {record.parkings?.map((p: any) => (
              <Tag key={p.id}>{p.name}</Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: 'Status',
      key: 'status',
      render: (_: any, record: any) => (
        <Tag color={record.isActive ? 'green' : 'red'}>
          {record.isActive ? 'Active' : 'Disabled'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => openEditModal(record)}
            size="small"
            title="Edit"
          />
          <Button
            icon={record.isActive ? <StopOutlined /> : <CheckCircleOutlined />}
            onClick={() => handleToggleActive(record)}
            size="small"
            danger={record.isActive}
            title={record.isActive ? 'Disable' : 'Enable'}
          />
          <Popconfirm
            title="Remove staff member?"
            description="This will permanently remove them from this organization."
            onConfirm={() => handleRemoveMembership(record.id)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Button
              icon={<DeleteOutlined />}
              danger
              size="small"
              title="Remove"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>Staff Management - {organizationName}</h1>
        <Button
          type="primary"
          icon={<UserAddOutlined />}
          onClick={() => {
            resetAddForm();
            setIsAddModalVisible(true);
          }}
        >
          Add Staff
        </Button>
      </div>

      <Table
        loading={loading}
        dataSource={memberships}
        columns={columns}
        rowKey="id"
        pagination={{ pageSize: 10 }}
      />

      {/* Add Staff Modal */}
      <Modal
        title="Add Staff Member"
        open={isAddModalVisible}
        onCancel={() => setIsAddModalVisible(false)}
        footer={null}
        width={500}
      >
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">
            Search for an existing user by email or phone number to add them as staff.
          </Text>
        </div>

        <Space.Compact style={{ width: '100%', marginBottom: 16 }}>
          <Select
            value={searchType}
            onChange={setSearchType}
            style={{ width: 120 }}
          >
            <Option value="email">Email</Option>
            <Option value="phone">Phone</Option>
          </Select>
          <Input
            placeholder={searchType === 'email' ? 'Enter email address' : 'Enter phone number'}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onPressEnter={handleSearch}
            style={{ flex: 1 }}
          />
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleSearch}
            loading={searching}
          >
            Search
          </Button>
        </Space.Compact>

        {searchError && (
          <Alert
            message={searchError}
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {foundUser && (
          <Card size="small" style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 8 }}>
              <Text strong>User Found:</Text>
            </div>
            <div>
              <Text>{foundUser.firstName} {foundUser.lastName}</Text>
            </div>
            <div>
              <Text type="secondary">{foundUser.email}</Text>
            </div>
            {foundUser.phone && (
              <div>
                <Text type="secondary">{foundUser.phone}</Text>
              </div>
            )}

            <Divider />

            <Form layout="vertical">
              <Form.Item label="Role">
                <Select value={selectedRole} onChange={setSelectedRole}>
                  <Option value="STAFF">Staff</Option>
                  <Option value="OWNER">Owner</Option>
                </Select>
              </Form.Item>

              <Form.Item label="Parking Access">
                <Checkbox.Group
                  value={selectedParkingIds}
                  onChange={(values) => setSelectedParkingIds(values as string[])}
                  style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
                >
                  {parkings.length === 0 ? (
                    <Text type="secondary">No parkings available</Text>
                  ) : (
                    parkings.map((p: any) => (
                      <Checkbox key={p.id} value={p.id}>
                        {p.name} ({p.code})
                      </Checkbox>
                    ))
                  )}
                </Checkbox.Group>
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary">
                    Leave empty to grant access to all parkings
                  </Text>
                </div>
              </Form.Item>

              <Button
                type="primary"
                onClick={handleAddStaff}
                loading={creating}
                block
              >
                Add as {selectedRole}
              </Button>
            </Form>
          </Card>
        )}

        {!foundUser && !searchError && !searching && (
          <Empty
            description="Search for a user to add them as staff"
            style={{ padding: 24 }}
          />
        )}
      </Modal>

      {/* Edit Membership Modal */}
      <Modal
        title="Edit Staff Member"
        open={isEditModalVisible}
        onOk={handleEditMembership}
        onCancel={() => setIsEditModalVisible(false)}
        confirmLoading={updating}
      >
        {editingMembership && (
          <Form layout="vertical">
            <div style={{ marginBottom: 16 }}>
              <Text strong>
                {editingMembership.user?.firstName} {editingMembership.user?.lastName}
              </Text>
              <br />
              <Text type="secondary">{editingMembership.user?.email}</Text>
            </div>

            <Form.Item label="Role">
              <Select value={selectedRole} onChange={setSelectedRole}>
                <Option value="STAFF">Staff</Option>
                <Option value="OWNER">Owner</Option>
              </Select>
            </Form.Item>

            <Form.Item label="Parking Access">
              <Checkbox.Group
                value={selectedParkingIds}
                onChange={(values) => setSelectedParkingIds(values as string[])}
                style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
              >
                {parkings.map((p: any) => (
                  <Checkbox key={p.id} value={p.id}>
                    {p.name} ({p.code})
                  </Checkbox>
                ))}
              </Checkbox.Group>
              <div style={{ marginTop: 8 }}>
                <Text type="secondary">
                  Leave empty to grant access to all parkings
                </Text>
              </div>
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );
};

// ==================== MEMBERSHIPS MODAL ====================
interface MembershipModalProps {
  userId: string;
  userEmail: string;
  userName: string;
  visible: boolean;
  onClose: () => void;
}

const MembershipModal: React.FC<MembershipModalProps> = ({ userId, userEmail, userName, visible, onClose }) => {
  const [isAddingMembership, setIsAddingMembership] = useState(false);
  const [editingMembership, setEditingMembership] = useState<any>(null);
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<'STAFF' | 'OWNER' | 'ADMIN'>('STAFF');
  const [selectedParkingIds, setSelectedParkingIds] = useState<string[]>([]);

  // Query user memberships
  const { loading, data, refetch } = useQuery(GET_USER_MEMBERSHIPS, {
    variables: { userId },
    skip: !visible,
    fetchPolicy: 'network-only',
  });

  // Query all organizations (for adding new membership)
  const { data: orgsData } = useQuery(GET_ALL_ORGANIZATIONS, {
    skip: !visible,
  });

  // Query parkings for selected org
  const { data: parkingsData } = useQuery(GET_ALL_LOCATIONS, {
    variables: { type: 'PARKING', organizationId: selectedOrgId },
    skip: !selectedOrgId,
  });

  // Mutations
  const [createMembership, { loading: creating }] = useMutation(CREATE_MEMBERSHIP);
  const [updateMembership, { loading: updating }] = useMutation(UPDATE_MEMBERSHIP);
  const [revokeMembership] = useMutation(REVOKE_MEMBERSHIP);

  const memberships = data?.getUserMemberships || [];
  const organizations = orgsData?.getAllOrganizations || [];
  const parkings = parkingsData?.getAllLocations || [];

  // Filter out orgs where user already has a membership
  const existingOrgIds = memberships.map((m: any) => m.organization?.id).filter(Boolean);
  const availableOrgs = organizations.filter((org: any) => !existingOrgIds.includes(org.id));

  const resetAddForm = () => {
    setSelectedOrgId(null);
    setSelectedRole('STAFF');
    setSelectedParkingIds([]);
    setIsAddingMembership(false);
  };

  const handleAddMembership = async () => {
    if (!selectedOrgId) {
      message.warning('Please select an organization');
      return;
    }

    try {
      await createMembership({
        variables: {
          input: {
            userId,
            organizationId: selectedOrgId,
            role: selectedRole,
            parkingIds: selectedParkingIds.length > 0 ? selectedParkingIds : null,
          },
        },
      });

      message.success('Membership added successfully');
      resetAddForm();
      refetch();
    } catch (error: any) {
      message.error(error.message);
    }
  };

  const handleEditMembership = async () => {
    if (!editingMembership) return;

    try {
      await updateMembership({
        variables: {
          id: editingMembership.id,
          input: {
            role: selectedRole,
            parkingIds: selectedParkingIds.length > 0 ? selectedParkingIds : null,
          },
        },
      });

      message.success('Membership updated successfully');
      setEditingMembership(null);
      refetch();
    } catch (error: any) {
      message.error(error.message);
    }
  };

  const handleToggleActive = async (membership: any) => {
    try {
      await updateMembership({
        variables: {
          id: membership.id,
          input: {
            isActive: !membership.isActive,
          },
        },
      });

      message.success(membership.isActive ? 'Membership disabled' : 'Membership enabled');
      refetch();
    } catch (error: any) {
      message.error(error.message);
    }
  };

  const handleRemoveMembership = async (membershipId: string) => {
    try {
      await revokeMembership({
        variables: { id: membershipId },
      });

      message.success('Membership removed');
      refetch();
    } catch (error: any) {
      message.error(error.message);
    }
  };

  const openEditModal = (membership: any) => {
    setEditingMembership(membership);
    setSelectedOrgId(membership.organization?.id);
    setSelectedRole(membership.role);
    setSelectedParkingIds(membership.parkingIds || []);
  };

  const membershipColumns = [
    {
      title: 'Organization',
      key: 'organization',
      render: (_: any, record: any) => record.organization?.name || '-',
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => {
        const colorMap: Record<string, string> = {
          ADMIN: 'red',
          OWNER: 'blue',
          STAFF: 'green',
        };
        return <Tag color={colorMap[role]}>{role}</Tag>;
      },
    },
    {
      title: 'Parkings',
      key: 'parkings',
      render: (_: any, record: any) => {
        if (!record.parkingIds || record.parkingIds.length === 0) {
          return <Tag color="purple">All Parkings</Tag>;
        }
        return (
          <Space wrap size={[4, 4]}>
            {record.parkings?.map((p: any) => (
              <Tag key={p.id}>{p.name}</Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: 'Status',
      key: 'status',
      render: (_: any, record: any) => (
        <Tag color={record.isActive ? 'green' : 'red'}>
          {record.isActive ? 'Active' : 'Disabled'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_: any, record: any) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => openEditModal(record)}
            size="small"
            title="Edit"
          />
          <Button
            icon={record.isActive ? <StopOutlined /> : <CheckCircleOutlined />}
            onClick={() => handleToggleActive(record)}
            size="small"
            danger={record.isActive}
            title={record.isActive ? 'Disable' : 'Enable'}
          />
          <Popconfirm
            title="Remove membership?"
            description="This will permanently remove this membership."
            onConfirm={() => handleRemoveMembership(record.id)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Button
              icon={<DeleteOutlined />}
              danger
              size="small"
              title="Remove"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Modal
      title={
        <Space>
          <KeyOutlined />
          <span>Manage Memberships</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={800}
    >
      {/* User Info Header */}
      <Card size="small" style={{ marginBottom: 16, background: '#fafafa' }}>
        <Space direction="vertical" size={0}>
          <Text strong>{userName || 'Unnamed User'}</Text>
          <Text type="secondary">{userEmail}</Text>
        </Space>
      </Card>

      {/* Current Memberships */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <Text strong>Current Memberships</Text>
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => setIsAddingMembership(true)}
            disabled={availableOrgs.length === 0}
          >
            Add Membership
          </Button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
        ) : memberships.length === 0 ? (
          <Empty description="No memberships" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <Table
            dataSource={memberships}
            columns={membershipColumns}
            rowKey="id"
            size="small"
            pagination={false}
          />
        )}
      </div>

      {/* Add Membership Form */}
      {isAddingMembership && (
        <Card size="small" title="Add New Membership" extra={
          <Button size="small" onClick={resetAddForm}>Cancel</Button>
        }>
          <Form layout="vertical">
            <Form.Item label="Organization" required>
              <Select
                placeholder="Select organization"
                value={selectedOrgId}
                onChange={(val) => {
                  setSelectedOrgId(val);
                  setSelectedParkingIds([]);
                }}
              >
                {availableOrgs.map((org: any) => (
                  <Option key={org.id} value={org.id}>{org.name}</Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label="Role" required>
              <Select value={selectedRole} onChange={setSelectedRole}>
                <Option value="STAFF">Staff</Option>
                <Option value="OWNER">Owner</Option>
                <Option value="ADMIN">Admin</Option>
              </Select>
            </Form.Item>

            {selectedOrgId && selectedRole !== 'ADMIN' && (
              <Form.Item label="Parking Access">
                <Checkbox.Group
                  value={selectedParkingIds}
                  onChange={(values) => setSelectedParkingIds(values as string[])}
                  style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
                >
                  {parkings.length === 0 ? (
                    <Text type="secondary">No parkings in this organization</Text>
                  ) : (
                    parkings.map((p: any) => (
                      <Checkbox key={p.id} value={p.id}>
                        {p.name} ({p.code})
                      </Checkbox>
                    ))
                  )}
                </Checkbox.Group>
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary">
                    Leave empty to grant access to all parkings
                  </Text>
                </div>
              </Form.Item>
            )}

            <Button
              type="primary"
              onClick={handleAddMembership}
              loading={creating}
              block
            >
              Add Membership
            </Button>
          </Form>
        </Card>
      )}

      {/* Edit Membership Form */}
      {editingMembership && (
        <Card size="small" title="Edit Membership" extra={
          <Button size="small" onClick={() => setEditingMembership(null)}>Cancel</Button>
        } style={{ marginTop: 16 }}>
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary">Organization: </Text>
            <Text strong>{editingMembership.organization?.name}</Text>
          </div>

          <Form layout="vertical">
            <Form.Item label="Role">
              <Select value={selectedRole} onChange={setSelectedRole}>
                <Option value="STAFF">Staff</Option>
                <Option value="OWNER">Owner</Option>
                <Option value="ADMIN">Admin</Option>
              </Select>
            </Form.Item>

            {selectedRole !== 'ADMIN' && (
              <Form.Item label="Parking Access">
                <Checkbox.Group
                  value={selectedParkingIds}
                  onChange={(values) => setSelectedParkingIds(values as string[])}
                  style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
                >
                  {parkings.map((p: any) => (
                    <Checkbox key={p.id} value={p.id}>
                      {p.name} ({p.code})
                    </Checkbox>
                  ))}
                </Checkbox.Group>
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary">
                    Leave empty to grant access to all parkings
                  </Text>
                </div>
              </Form.Item>
            )}

            <Button
              type="primary"
              onClick={handleEditMembership}
              loading={updating}
              block
            >
              Update Membership
            </Button>
          </Form>
        </Card>
      )}
    </Modal>
  );
};

// ==================== ADMIN USER VIEW ====================
const AdminUserView: React.FC = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [membershipUser, setMembershipUser] = useState<any>(null);
  const [form] = Form.useForm();

  const { loading, data, refetch } = useQuery(GET_ALL_USERS);
  const [createUser] = useMutation(CREATE_USER);
  const [updateUser] = useMutation(UPDATE_USER);
  const [deleteUser] = useMutation(DELETE_USER);

  const handleAdd = () => {
    setEditingUser(null);
    form.resetFields();
    form.setFieldsValue({ enabled: true });
    setIsModalVisible(true);
  };

  const handleEdit = (record: any) => {
    setEditingUser(record);
    form.setFieldsValue({
      email: record.email,
      firstName: record.firstName,
      lastName: record.lastName,
      phone: record.phone,
      address: record.address,
      enabled: record.enabled,
    });
    setIsModalVisible(true);
  };

  const handleDelete = async (record: any) => {
    try {
      await deleteUser({ variables: { id: record.id } });

      // Show appropriate success message based on deletion step
      if (record.enabled) {
        message.success('User account has been disabled. Click delete again to permanently remove from Cognito.');
      } else if (record.deletedAt) {
        message.success('User has been permanently deleted from AWS Cognito. Database record preserved for history.');
      }

      refetch();
    } catch (error: any) {
      message.error(error.message);
    }
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();

      if (editingUser) {
        await updateUser({
          variables: {
            id: editingUser.id,
            input: values,
          },
        });
        message.success('User updated successfully');
      } else {
        await createUser({
          variables: {
            input: {
              ...values,
              role: 'CLIENT', // New users start as CLIENT by default
              cognitoSub: `manual_${Date.now()}`, // Generate a temporary cognito sub for MVP
            },
          },
        });
        message.success('User created successfully. Add memberships to grant access.');
      }

      setIsModalVisible(false);
      refetch();
    } catch (error: any) {
      message.error(error.message);
    }
  };

  const columns = [
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Name',
      key: 'name',
      render: (_: any, record: any) => `${record.firstName || ''} ${record.lastName || ''}`.trim() || '-',
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      render: (phone: string) => phone || '-',
    },
    {
      title: 'Legacy Role',
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => {
        const colorMap: Record<string, string> = {
          ADMINISTRATOR: 'red',
          OWNER: 'blue',
          STAFF: 'green',
          CLIENT: 'default',
        };
        return <Tag color={colorMap[role]}>{role}</Tag>;
      },
    },
    {
      title: 'Status',
      key: 'status',
      render: (_: any, record: any) => {
        const isReactivated = record.metadata?.reactivated;
        const isCognitoDeleted = record.metadata?.cognitoDeleted;

        if (record.deletedAt && isCognitoDeleted) {
          return (
            <Space>
              <Tag color="red">Disabled</Tag>
              <Tag color="purple">Deleted from Cognito</Tag>
            </Space>
          );
        }

        if (record.deletedAt) {
          return (
            <Space>
              <Tag color="red">Disabled</Tag>
              <Tag color="orange">Pending Deletion</Tag>
            </Space>
          );
        }

        if (isReactivated) {
          return (
            <Space>
              <Tag color="green">Active</Tag>
              <Tag color="blue">Reactivated</Tag>
            </Space>
          );
        }

        return <Tag color={record.enabled ? 'green' : 'red'}>{record.enabled ? 'Active' : 'Disabled'}</Tag>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => {
        const isEnabled = record.enabled;
        const isDeleted = record.deletedAt != null;

        let confirmTitle = '';
        let confirmDescription = '';

        if (isEnabled) {
          confirmTitle = 'Disable this account?';
          confirmDescription = 'This will prevent login but preserve all data. You can delete permanently in a second step.';
        } else if (isDeleted) {
          confirmTitle = 'Permanently delete from Cognito?';
          confirmDescription = 'This account is already disabled. This action will permanently remove the user from AWS Cognito. Database records will be preserved for history. This cannot be undone.';
        } else {
          confirmTitle = 'Delete this user?';
          confirmDescription = 'This action will disable the account.';
        }

        return (
          <Space>
            <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} size="small" title="Edit User" />
            <Button
              icon={<KeyOutlined />}
              onClick={() => setMembershipUser(record)}
              size="small"
              title="Manage Memberships"
            />
            <Popconfirm
              title={confirmTitle}
              description={confirmDescription}
              onConfirm={() => handleDelete(record)}
              okText="Yes"
              cancelText="No"
              okButtonProps={{ danger: true }}
            >
              <Button
                icon={<DeleteOutlined />}
                danger
                size="small"
                type={isDeleted ? 'primary' : 'default'}
                title="Delete"
              />
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <h1>Users</h1>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Add User
        </Button>
      </div>

      <Alert
        message="User Management"
        description="Use the Edit button to modify user details. Use the Key button to manage memberships (access to organizations and parkings)."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Table
        loading={loading}
        dataSource={data?.getAllUsers || []}
        columns={columns}
        rowKey="id"
        pagination={{ pageSize: 10 }}
      />

      {/* User Edit Modal - Basic Info Only */}
      <Modal
        title={editingUser ? 'Edit User' : 'Add User'}
        open={isModalVisible}
        onOk={handleOk}
        onCancel={() => setIsModalVisible(false)}
        width={500}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: 'Please input email!' },
              { type: 'email', message: 'Please input valid email!' },
            ]}
          >
            <Input disabled={!!editingUser} />
          </Form.Item>

          <Form.Item label="First Name" name="firstName">
            <Input />
          </Form.Item>

          <Form.Item label="Last Name" name="lastName">
            <Input />
          </Form.Item>

          <Form.Item label="Phone" name="phone">
            <Input />
          </Form.Item>

          <Form.Item label="Address" name="address">
            <Input.TextArea rows={2} />
          </Form.Item>

          <Form.Item label="Status" name="enabled">
            <Select>
              <Option value={true}>Active</Option>
              <Option value={false}>Disabled</Option>
            </Select>
          </Form.Item>

          {!editingUser && (
            <Alert
              message="New users are created as CLIENT. Use the Memberships button after creation to assign roles and organization access."
              type="info"
              showIcon
            />
          )}
        </Form>
      </Modal>

      {/* Membership Modal */}
      {membershipUser && (
        <MembershipModal
          userId={membershipUser.id}
          userEmail={membershipUser.email}
          userName={`${membershipUser.firstName || ''} ${membershipUser.lastName || ''}`.trim()}
          visible={!!membershipUser}
          onClose={() => setMembershipUser(null)}
        />
      )}
    </div>
  );
};

// ==================== OWNER MULTI-ORG WRAPPER ====================
interface OwnerOrg {
  id: string;
  name: string;
}

const OwnerMultiOrgView: React.FC<{ organizations: OwnerOrg[] }> = ({ organizations }) => {
  // If only one org, show simple view without tabs
  if (organizations.length === 1) {
    return (
      <OwnerStaffView
        organizationId={organizations[0].id}
        organizationName={organizations[0].name}
      />
    );
  }

  // Multiple orgs - show tabs
  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>Staff Management</h1>
        <Tag color="blue" style={{ fontSize: 14, padding: '4px 12px' }}>
          <TeamOutlined /> {organizations.length} Organizations
        </Tag>
      </div>

      <Tabs
        defaultActiveKey={organizations[0].id}
        type="card"
        size="large"
        items={organizations.map(org => ({
          key: org.id,
          label: (
            <span>
              <BankOutlined style={{ marginRight: 8 }} />
              {org.name}
            </span>
          ),
          children: (
            <OwnerStaffView
              organizationId={org.id}
              organizationName={org.name}
            />
          ),
        }))}
      />
    </div>
  );
};

// ==================== MAIN USERS COMPONENT ====================
export const Users: React.FC = () => {
  const { isAdmin, memberships, legacyOrganization, getHighestRole, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  // ADMIN sees full user management
  if (isAdmin()) {
    return <AdminUserView />;
  }

  // OWNER sees staff management - collect all owner memberships
  const ownerMemberships = memberships.filter(m => m.role === 'OWNER' && m.isActive && m.organization);

  if (ownerMemberships.length > 0) {
    const organizations: OwnerOrg[] = ownerMemberships.map(m => ({
      id: m.organization!.id,
      name: m.organization!.name,
    }));
    return <OwnerMultiOrgView organizations={organizations} />;
  }

  // Fallback: Legacy OWNER users without memberships
  if (getHighestRole() === 'OWNER' && legacyOrganization) {
    return (
      <OwnerStaffView
        organizationId={legacyOrganization.id}
        organizationName={legacyOrganization.name}
      />
    );
  }

  // STAFF or others - no access to user management
  return (
    <div style={{ textAlign: 'center', padding: 48 }}>
      <Alert
        message="Access Restricted"
        description="You do not have permission to manage users. Please contact your administrator."
        type="warning"
        showIcon
      />
    </div>
  );
};
